/**
 * Editing Toolbar
 *
 * Generic toolbar system for canvas editing plugins.
 * Creates a floating toolbar above the edited node with configurable buttons.
 * Handles DOM creation, event isolation, positioning, and active state tracking.
 */

// =============================================================================
// Types
// =============================================================================

export type ToolbarEntry = ToolbarButton | ToolbarSeparator;

export interface ToolbarButton {
	type: 'button';
	id: string;
	label: string;
	title: string;
	isActive: boolean;
	onActivate: () => void;
	/** When set, renders as a colored circle swatch instead of a text label */
	color?: string;
}

export interface ToolbarSeparator {
	type: 'separator';
}

export interface EditingToolbar {
	/** Update toolbar entries (active states, add/remove buttons) */
	update(entries: ToolbarEntry[]): void;
	/** Reposition toolbar above the node element */
	updatePosition(): void;
	/** Remove toolbar from DOM and clean up listeners */
	destroy(): void;
}

// =============================================================================
// Event isolation
// =============================================================================

/** Events to isolate from the canvas input system */
const ISOLATED_EVENTS = [
	'keydown',
	'keyup',
	'mousedown',
	'mouseup',
	'click',
	'dblclick',
	'pointerdown',
	'pointerup',
] as const;

// =============================================================================
// Implementation
// =============================================================================

/**
 * Create a floating editing toolbar above a node element.
 *
 * @param entries - Toolbar buttons and separators
 * @param nodeElement - The .canvas-node element (for positioning)
 * @param container - The canvas container element (toolbar is appended here)
 */
export function createEditingToolbar(
	entries: ToolbarEntry[],
	nodeElement: HTMLElement,
	container: HTMLElement,
): EditingToolbar {
	const toolbar = document.createElement('div');
	toolbar.className = 'editing-toolbar';

	// Render initial entries
	renderEntries(toolbar, entries);

	// Append to container (outside canvas transform layer)
	container.appendChild(toolbar);

	// --- Event isolation ---
	// Stop all events from bubbling up to the canvas input system
	function stopBubble(e: Event) {
		e.stopPropagation();
	}

	for (const evt of ISOLATED_EVENTS) {
		toolbar.addEventListener(evt, stopBubble);
	}

	// --- Focus preservation ---
	// Prevent mousedown from stealing focus from the editor
	toolbar.addEventListener('mousedown', (e) => {
		e.preventDefault();
	});

	// --- Click handling ---
	toolbar.addEventListener('click', (e) => {
		const button = (e.target as HTMLElement).closest('button[data-id]') as HTMLElement | null;
		if (!button) return;

		const id = button.dataset.id;
		if (!id) return;

		// Find matching entry in current entries
		const entry = currentEntries.find(
			(ent): ent is ToolbarButton => ent.type === 'button' && ent.id === id,
		);
		entry?.onActivate();
	});

	// Track current entries for click handler lookup
	let currentEntries = entries;

	// --- Positioning ---
	function updatePosition() {
		const nodeRect = nodeElement.getBoundingClientRect();
		const containerRect = container.getBoundingClientRect();

		const nodeCenterX = nodeRect.left + nodeRect.width / 2 - containerRect.left;
		const left = nodeCenterX - toolbar.offsetWidth / 2;
		const top = nodeRect.top - containerRect.top - toolbar.offsetHeight - 8;

		toolbar.style.left = `${left}px`;
		toolbar.style.top = `${top}px`;
	}

	// Initial position after layout
	requestAnimationFrame(updatePosition);

	return {
		update(newEntries: ToolbarEntry[]) {
			currentEntries = newEntries;
			updateEntries(toolbar, newEntries);
		},

		updatePosition,

		destroy() {
			for (const evt of ISOLATED_EVENTS) {
				toolbar.removeEventListener(evt, stopBubble);
			}
			toolbar.remove();
		},
	};
}

// =============================================================================
// DOM helpers
// =============================================================================

/** Render entries into a toolbar element (initial build) */
function renderEntries(toolbar: HTMLElement, entries: ToolbarEntry[]): void {
	for (const entry of entries) {
		if (entry.type === 'separator') {
			const sep = document.createElement('div');
			sep.className = 'toolbar-separator';
			toolbar.appendChild(sep);
		} else if (entry.color) {
			const btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'toolbar-color-swatch';
			btn.title = entry.title;
			btn.dataset.id = entry.id;
			if (entry.isActive) {
				btn.classList.add('active');
			}
			const dot = document.createElement('span');
			dot.className = 'toolbar-swatch-dot';
			dot.style.backgroundColor = entry.color;
			btn.appendChild(dot);
			toolbar.appendChild(btn);
		} else {
			const btn = document.createElement('button');
			btn.type = 'button';
			btn.textContent = entry.label;
			btn.title = entry.title;
			btn.dataset.id = entry.id;
			if (entry.isActive) {
				btn.classList.add('active');
			}
			toolbar.appendChild(btn);
		}
	}
}

/**
 * Update toolbar entries efficiently.
 * If the structure matches (same ids/separators in order), just toggle active states.
 * Otherwise, rebuild the DOM.
 */
function updateEntries(toolbar: HTMLElement, entries: ToolbarEntry[]): void {
	const children = Array.from(toolbar.children) as HTMLElement[];

	// Check if structure matches
	if (children.length === entries.length && structureMatches(children, entries)) {
		// Fast path: just update active states
		let childIdx = 0;
		for (const entry of entries) {
			if (entry.type === 'button') {
				children[childIdx].classList.toggle('active', entry.isActive);
			}
			childIdx++;
		}
	} else {
		// Slow path: rebuild
		toolbar.innerHTML = '';
		renderEntries(toolbar, entries);
	}
}

/** Check if existing DOM children match the entry structure */
function structureMatches(children: HTMLElement[], entries: ToolbarEntry[]): boolean {
	for (let i = 0; i < entries.length; i++) {
		const entry = entries[i];
		const child = children[i];
		if (entry.type === 'separator') {
			if (!child.classList.contains('toolbar-separator')) return false;
		} else {
			if (child.tagName !== 'BUTTON') return false;
			if (child.dataset.id !== entry.id) return false;
		}
	}
	return true;
}
